import { Link } from "react-router-dom";
import { useTheme } from "@/components/ThemeContext";
import SEOHead from "@/components/SEOHead";
import { ChiSiamoStructuredData } from "@/components/StructuredData";
import { Shield, Award, Heart, Clock, Phone, MessageCircle } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const CHAUFFEUR_IMG = "https://images.pexels.com/photos/7594130/pexels-photo-7594130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const values = [
  {
    icon: Shield,
    title: "Professionalita'",
    desc: "Ogni nostro autista è selezionato con cura, formato per garantire un servizio impeccabile e discreto. La vostra sicurezza e il vostro comfort sono la nostra priorità assoluta.",
  },
  {
    icon: Heart,
    title: "Attenzione al Cliente",
    desc: "Ascoltiamo ogni vostra esigenza per creare un'esperienza di viaggio personalizzata. Dal primo contatto all'arrivo a destinazione, ogni dettaglio è curato.",
  },
  {
    icon: Award,
    title: "Esperienza nel Settore",
    desc: "Anni di esperienza nel trasporto di lusso ci hanno insegnato che l'eccellenza si costruisce giorno dopo giorno, viaggio dopo viaggio.",
  },
  {
    icon: Clock,
    title: "Posizionamento Luxury",
    desc: "Non offriamo semplicemente un trasporto. Offriamo un'esperienza. Veicoli premium, interni curati, autisti in abito elegante: il viaggio inizia dal momento in cui salite a bordo.",
  },
];

export default function ChiSiamoPage() {
  const { isDark } = useTheme();
  return (
    <main data-testid="chi-siamo-page" className="pt-20">
      <SEOHead
        title="Chi Siamo"
        description="Transfer Avellino: professionalita', esperienza e attenzione al cliente nel noleggio con conducente. Scopri la nostra storia, i nostri valori e perche' scegliere il nostro servizio NCC premium ad Avellino."
        path="/chi-siamo"
      />
      <ChiSiamoStructuredData />
      {/* Hero */}
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <ScrollReveal>
                <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-6">
                  Chi Siamo
                </span>
                <h1 className={`text-4xl sm:text-5xl lg:text-6xl tracking-tight font-normal leading-[1.1] mb-8 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
                  Affidati a un servizio che fa
                  <span className="italic text-[#C9A646]"> la differenza</span>
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <p className={`text-base leading-relaxed mb-6 ${isDark ? "text-[#A1A1AA]" : "text-[#555]"}`}>
                  Transfer Avellino nasce dalla passione per l'eccellenza nel trasporto.
                  Siamo un servizio di noleggio con conducente che ha fatto della qualità
                  il proprio marchio di fabbrica.
                </p>
                <p className={`text-base leading-relaxed mb-8 ${isDark ? "text-[#A1A1AA]" : "text-[#555]"}`}>
                  Ogni viaggio con noi e' un'esperienza su misura. Dalla scelta del veicolo
                  alla cura di ogni dettaglio, ci impegniamo per superare le vostre aspettative.
                  Il nostro obiettivo? Che al termine di ogni corsa vogliate affidarvi
                  nuovamente a noi.
                </p>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/prenota"
                    data-testid="chi-siamo-prenota-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                    Richiedi Preventivo
                  </Link>
                  <a
                    href="tel:+393784035054"
                    data-testid="chi-siamo-call-btn"
                    className="inline-flex items-center justify-center gap-2 border border-[#C9A646] text-[#C9A646] px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#C9A646]/10 transition-all duration-300"
                  >
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    Chiama Ora
                  </a>
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal delay={0.2}>
              <div className="relative">
                <img
                  src={CHAUFFEUR_IMG}
                  alt="Autista professionale Transfer Avellino"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 border-l-2 border-b-2 border-[#C9A646]" />
                <div className="absolute -top-4 -right-4 w-24 h-24 border-t-2 border-r-2 border-[#C9A646]" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0E0E0E]" : "bg-[#EDE8DC]"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-4">
              I Nostri Valori
            </span>
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-normal mb-16 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
              Cosa ci rende
              <span className="italic text-[#C9A646]"> unici</span>
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <ScrollReveal key={v.title} delay={i * 0.1}>
                  <div className={`border p-8 md:p-10 hover:border-[#C9A646]/30 transition-all duration-500 h-full ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}>
                    <Icon className="w-8 h-8 text-[#C9A646] mb-6" strokeWidth={1.5} />
                    <h3 className={`text-xl font-medium mb-3 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>{v.title}</h3>
                    <p className={`text-sm leading-relaxed ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>{v.desc}</p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-normal mb-4 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
              Pronti a vivere un viaggio
              <span className="italic text-[#C9A646]"> indimenticabile?</span>
            </h2>
            <p className={`mb-10 ${isDark ? "text-[#A1A1AA]" : "text-[#666]"}`}>
              Contattateci oggi per scoprire come possiamo rendere il vostro prossimo spostamento un'esperienza unica.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <Link
              to="/prenota"
              data-testid="chi-siamo-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
            >
              Prenota il tuo Transfer
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
